import { useId, useRef, useState, type ReactNode } from "react";
import { ProgramSchema, type AccountErasureRequest, type EmailChoices, type Program } from "@pushback-and-chaos/contracts";
import { ACCOUNT_DATA, COACH_DATA, CONSENT, EMAIL_CHOICES, ERASURE, LEGAL_LINKS, PROPOSED } from "../design/copy.js";
import type { ErasureConfirmResult } from "../data/account-erasure.js";
import { AccountErasureDialog } from "../ui/account-erasure.js";
import { NotYet, Pill, Rule } from "../ui/primitives.js";
import { EmailChoicesFields, type EmailChoicesFor } from "./email-choices.js";

/** The settings section's email choices: the same saved set the desk card shows. */
export interface EmailChoicesSettings {
  readonly state: EmailChoicesFor;
  /** True while an email save from either surface is pending; this section's controls wait for it. */
  readonly saving?: boolean;
  readonly onSave: (choices: EmailChoices) => Promise<void>;
  readonly onSignIn: () => void;
}

/**
 * "delete my account." (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md), given only for a signed-in
 * account. `onConfirm` answers whether the request was made, was not, or could not be told, so the
 * dialog can say which when it stays.
 */
export interface AccountErasureDoor {
  /** GET /me/erasure's word on the address; null until it is read or when the read failed (the dialog then offers the downloads, saying nothing about the address). */
  readonly emailVerified: boolean | null;
  readonly onConfirm: (choices: AccountErasureRequest) => Promise<ErasureConfirmResult>;
  /** The dialog's two downloads answer whether the copy was saved: the dialog says when it was not. */
  readonly onDownloadCoaching: () => Promise<boolean>;
  readonly onDownloadAccount: () => Promise<boolean>;
}

/** Program and season as the sheet edits them: a field left out is unchanged, `null` clears it. */
export interface RecruitingFields {
  readonly program: Program | null;
  readonly targetSeason: string | null;
  readonly onChange: (patch: { program?: Program | null; targetSeason?: string | null }) => void;
}

/**
 * The `settings` dropdown from the input's control row, and `settings.` from
 * the landing nav — one sheet, reached from both.
 *
 * Two switches. The mechanics toggle is the ADR-0001 opt-in, described in the
 * words the ADR uses and defaulting off. Read-aloud (ADR 0011) is the other:
 * a rendering of the narration, never a mechanic wearing a different hat, and
 * shown as "not yet." where this server has no voice. Everything else the
 * product could expose as a preference would be one.
 *
 * Below them, what the coach keeps (Law 5, coach plan step 9): a copy on
 * request, and removal whole behind one confirmation. The controls are
 * shown only when the sheet is given the two callbacks, which is when the
 * learner has a coach to have kept anything.
 *
 * Then the email choices (2026-10-07), the desk card's seven, shown only once
 * the learner's first Harborlight report exists; empty and disabled, with the
 * guest note and "sign in.", for a guest. Program and season (CME stage 1)
 * sit on the confirmed profile, so they show only when the sheet is given
 * `recruiting`, which is when there is one.
 *
 * Last, below the account's download, "delete my account." (2026-10-20), for
 * a signed-in account only: the sheet is given `accountErasure` then.
 */
export function SettingsSheet({
  showMechanics,
  readAloud,
  narrationAvailable,
  onToggle,
  onToggleReadAloud,
  onClose,
  busy,
  emailChoices,
  recruiting,
  onExportAccount,
  onExportCoachData,
  onDeleteCoachData,
  accountErasure,
}: {
  showMechanics: boolean;
  readAloud: boolean;
  narrationAvailable: boolean;
  onToggle: (next: boolean) => void;
  onToggleReadAloud: (next: boolean) => void;
  onClose: () => void;
  busy: boolean;
  /** The email choices, given only once the learner's first Harborlight report exists (the server's `eligible`); before then settings shows none. */
  emailChoices?: EmailChoicesSettings | undefined;
  recruiting?: RecruitingFields | undefined;
  /** Whatever it answers (whether a copy was saved) is not this door's to say: the app's notice says a failure. */
  onExportAccount?: (() => Promise<unknown>) | undefined;
  onExportCoachData?: (() => Promise<void>) | undefined;
  onDeleteCoachData?: (() => Promise<void>) | undefined;
  accountErasure?: AccountErasureDoor | undefined;
}): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [erasing, setErasing] = useState(false);
  const erasureRowRef = useRef<HTMLDivElement | null>(null);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "color-mix(in srgb, var(--pc-ink) 28%, transparent)" }}>
      <div className="max-h-svh w-[34rem] max-w-full overflow-y-auto bg-paper px-6 py-8 sm:px-12 sm:py-10">
        <h2 className="m-0 font-sans text-nav">settings</h2>
        <Rule className="mt-2" />

        <label className="mt-8 flex cursor-pointer items-start gap-4">
          <input
            type="checkbox"
            checked={showMechanics}
            disabled={busy}
            onChange={(e) => onToggle(e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="block font-sans text-body">show mechanics</span>
            <span className="mt-1 block font-sans text-micro text-ink-soft">
              Off by default. Turning this on shows the numbers behind each turn as it happens. It changes nothing about
              what happens — the record is written before the outcome exists either way, and it is always available
              afterwards.
            </span>
          </span>
        </label>

        <label className={["mt-6 flex items-start gap-4", narrationAvailable ? "cursor-pointer" : "cursor-not-allowed"].join(" ")}>
          <input
            type="checkbox"
            checked={readAloud && narrationAvailable}
            disabled={busy || !narrationAvailable}
            onChange={(e) => onToggleReadAloud(e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="block font-sans text-body">
              {narrationAvailable ? PROPOSED.readAloud : <NotYet>{PROPOSED.readAloud}</NotYet>}
            </span>
            <span className="mt-1 block font-sans text-micro text-ink-soft">
              {narrationAvailable ? PROPOSED.readAloudHint : PROPOSED.readAloudUnavailable}
            </span>
          </span>
        </label>

        {emailChoices !== undefined ? (
          <div data-testid="emails" className="mt-8">
            <Rule />
            <h3 className="mt-6 mb-0 font-sans text-body">{EMAIL_CHOICES.settingsHeading}</h3>
            <p className="mt-1 mb-0 font-sans text-micro text-ink-soft">{EMAIL_CHOICES.settingsIntro}</p>
            <EmailChoicesFields state={emailChoices.state} busy={busy || (emailChoices.saving ?? false)}seasonSet={(recruiting?.targetSeason ?? null) !== null} onSave={emailChoices.onSave} onSignIn={emailChoices.onSignIn} />
          </div>
        ) : null}

        {recruiting !== undefined ? (
          <div data-testid="recruiting" className="mt-8">
            <Rule />
            <label className="mt-6 block">
              <span className="block font-sans text-body">{CONSENT.program}</span>
              <select
                value={recruiting.program ?? ""}
                disabled={busy}
                onChange={(e) => recruiting.onChange({ program: ProgramSchema.options.find((p) => p === e.target.value) ?? null })}
                className="mt-2 font-sans text-body"
              >
                <option value="">{CONSENT.programBlank}</option>
                {ProgramSchema.options.map((program) => (
                  <option key={program} value={program}>
                    {CONSENT.programOptions[program]}
                  </option>
                ))}
              </select>
              <span className="mt-1 block font-sans text-micro text-ink-soft">{CONSENT.programHint}</span>
            </label>
            <SeasonField key={recruiting.targetSeason ?? ""} value={recruiting.targetSeason} disabled={busy} onSave={(targetSeason) => recruiting.onChange({ targetSeason })} />
          </div>
        ) : null}

        {onExportCoachData !== undefined && onDeleteCoachData !== undefined ? (
          <div data-testid="coach-data" className="mt-8">
            <Rule />
            <h3 className="mt-6 mb-0 font-sans text-body">{COACH_DATA.heading}</h3>
            <p className="mt-1 mb-0 font-sans text-micro text-ink-soft">{removed ? COACH_DATA.removed : confirming ? COACH_DATA.confirmBody : COACH_DATA.body}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {confirming ? (
                <>
                  <Pill disabled={busy} onClick={() => { void onDeleteCoachData().then(() => { setConfirming(false); setRemoved(true); }); }}>{COACH_DATA.confirm}</Pill>
                  <Pill disabled={busy} onClick={() => setConfirming(false)}>{COACH_DATA.cancel}</Pill>
                </>
              ) : (
                <>
                  <Pill disabled={busy} onClick={() => void onExportCoachData()}>{COACH_DATA.export}</Pill>
                  <Pill disabled={busy || removed} onClick={() => setConfirming(true)}>{COACH_DATA.remove}</Pill>
                </>
              )}
            </div>
          </div>
        ) : null}

        {onExportAccount !== undefined ? (
          <div data-testid="account-data" className="mt-8">
            <Rule />
            <h3 className="mt-6 mb-0 font-sans text-body">{ACCOUNT_DATA.heading}</h3>
            <p className="mt-1 mb-0 font-sans text-micro text-ink-soft">{ACCOUNT_DATA.body}</p>
            <div className="mt-4">
              <Pill disabled={busy} onClick={() => void onExportAccount()}>{ACCOUNT_DATA.export}</Pill>
            </div>
          </div>
        ) : null}

        {accountErasure !== undefined ? (
          <div data-testid="account-erasure" className="mt-8">
            <Rule />
            <h3 className="mt-6 mb-0 font-sans text-body">{ERASURE.heading}</h3>
            <p className="mt-1 mb-0 font-sans text-micro text-ink-soft">{ERASURE.body}</p>
            <div ref={erasureRowRef} className="mt-4">
              <Pill disabled={busy} onClick={() => setErasing(true)}>{ERASURE.door}</Pill>
            </div>
          </div>
        ) : null}
        {accountErasure !== undefined && erasing ? (
          <AccountErasureDialog
            emailVerified={accountErasure.emailVerified}
            busy={busy}
            onConfirm={accountErasure.onConfirm}
            onDownloadCoaching={accountErasure.onDownloadCoaching}
            onDownloadAccount={accountErasure.onDownloadAccount}
            onCancel={() => {
              setErasing(false);
              // Focus goes back to the door the dialog came from.
              erasureRowRef.current?.querySelector("button")?.focus();
            }}
          />
        ) : null}

        {/* The privacy and terms pages (2026-10-22), quiet beside done., each in a new tab so play is never left. */}
        <div data-testid="settings-foot" className="mt-10 flex items-center justify-between gap-6">
          <div className="flex gap-4 font-sans text-micro">
            <a href="#/privacy" target="_blank" rel="noopener noreferrer" className="text-ink-soft underline underline-offset-2 hover:bg-marker">
              {LEGAL_LINKS.privacy}
            </a>
            <a href="#/terms" target="_blank" rel="noopener noreferrer" className="text-ink-soft underline underline-offset-2 hover:bg-marker">
              {LEGAL_LINKS.terms}
            </a>
          </div>
          <Pill onClick={onClose}>done.</Pill>
        </div>
      </div>
    </div>
  );
}

/** Stored as `2026-27`, shown and typed as `2026–27`; either dash is read. */
const SEASON = /^([0-9]{4})[-–]([0-9]{2})$/;

/**
 * The season saves when the field is left. Empty clears it; anything that is
 * not a season is marked invalid and not sent, so the stored value stands.
 * Keyed on the stored value by its parent, so a saved change resets the draft.
 */
function SeasonField({ value, disabled, onSave }: { value: string | null; disabled: boolean; onSave: (next: string | null) => void }): ReactNode {
  const shown = value === null ? "" : value.replace("-", "–");
  const [draft, setDraft] = useState(shown);
  const [invalid, setInvalid] = useState(false);
  const hintId = useId();
  const commit = (): void => {
    const text = draft.trim();
    if (text === shown) {
      setInvalid(false);
      return;
    }
    if (text === "") {
      setInvalid(false);
      onSave(null);
      return;
    }
    const match = SEASON.exec(text);
    if (match === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onSave(`${match[1] ?? ""}-${match[2] ?? ""}`);
  };
  return (
    <label className="mt-6 block">
      <span className="block font-sans text-body">{CONSENT.season}</span>
      <input
        type="text"
        value={draft}
        placeholder={CONSENT.seasonExample}
        disabled={disabled}
        aria-invalid={invalid}
        aria-describedby={invalid ? hintId : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
        className={["mt-2 w-32 font-sans text-body", invalid ? "outline outline-1 outline-ink" : ""].join(" ")}
      />
      {invalid ? (
        <span id={hintId} className="mt-1 block font-sans text-micro text-ink">
          {CONSENT.seasonInvalid}
        </span>
      ) : null}
      <span className="mt-1 block font-sans text-micro text-ink-soft">{CONSENT.seasonHint}</span>
    </label>
  );
}
